import React, { useEffect, useState } from "react";
import PrivateLayout from "../../layouts/privateLayout";
import { Backdrop, Box, Button, Fade, IconButton, Modal, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import BasicBreadcrumbs from "../../components/breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { Add, Close } from "@mui/icons-material";
import toast from "react-hot-toast";
import { useApiClient } from "../../context/ApiClientContext";
import Listing from "./listing";
import api from "../../helper/baseApi";
import ErrorAlert from "../../helper/errorAlert";
import ComponentBackdrop from "../../components/backdrop";
import Form from "./form";
import ConfirmDialog from "../../helper/confirmDialog";

export const DataContext = React.createContext();

const Index = () => {

  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showButton, setShowButton] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filteredDataList, setFilteredDataList] = React.useState([]);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);

  const { apiUrls: { mobileApiUrl } } = useApiClient();
  const navigate = useNavigate();

  const handleOpenConfirm = () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one department to delete.");
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleShowButton = (ids) => {
    setSelectedIds(ids);
    setShowButton(ids.length > 0);
  };

  useEffect(() => {
    fetchDataAndUpdateList();
  }, [location.pathname]);

  const fetchDataAndUpdateList = async () => {
    try {
      const apiUrl = `${mobileApiUrl}department/view`;
      const response = await api.get(apiUrl);

      const departmentList = response?.data?.data?.departmentList || [];
      const dataWithKeys = departmentList.map((item) => ({
        ...item,
        id: item.departmentId
      }));

      setFilteredDataList(dataWithKeys);
    } catch (error) {
      if (error?.response?.status === 404) {
        setFilteredDataList([]);
      } else {
        console.error("An error occurred:", error?.response?.statusText);
      }
    }
  };

  const handleConfirmDelete = async () => {
    setOpenBackdrop(true);
    try {
      const apiUrl = `${mobileApiUrl}department/delete`;
      const response = await api.delete(apiUrl, {
        data: { departmentId: selectedIds },
      });

      if (response.status === 200) {
        const deletedItems = filteredDataList.filter(item =>
          selectedIds.includes(item.id)
        );
        const deletedNames = deletedItems.map(item => item.name);
        setTimeout(() => {

          toast.success(`Deleted: ${deletedNames.join(", ")}`);

          setFilteredDataList(prev =>
            prev.filter(item => !selectedIds.includes(item.id))
          );
          setSelectedIds([]);
          setOpenConfirm(false);
        }, 2000);
      } else {
        toast.error("Failed to delete departments. Please try again.");
      }
    } catch (error) {
      setTimeout(() => {
        ErrorAlert(error, navigate);
      }, 2000);
    } finally {
      setTimeout(() => {
        setOpenBackdrop(false);
      }, 2000);
    }
  };

  const openModalFn = (data) => {
    setShowModal(true);
    setMode('create');
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const openEditModal = async (id) => {
    try {
      const apiUrl = `${mobileApiUrl}department/details?departmentId=${id}`;
      const response = await api.get(apiUrl);
      const details = response.data.data;
      setSelectedDept(details);
      setShowModal(true);
      setMode('edit');

    } catch (error) {
      ErrorAlert(error, navigate);
    }
  };

  return (
    <PrivateLayout>
      <Box sx={{ mx: "40px", mt: 5 }}>
        <ComponentBackdrop openBackdrop={openBackdrop} />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <BasicBreadcrumbs first="Department" />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
            {showButton && (
              <Button
                variant="contained"
                size="large"
                fullWidth={isMobile}
                sx={{
                  backgroundColor: "secondary.main",
                  fontWeight: "600",
                  "&:hover": { backgroundColor: "secondary.dark" },
                  whiteSpace: "nowrap",
                }}
                onClick={handleOpenConfirm}
              >
                Delete
              </Button>
            )}

            <ConfirmDialog
              open={openConfirm}
              onClose={handleCloseConfirm}
              onConfirm={handleConfirmDelete}
              title="Delete Department"
              message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these departments" : "this department"
                }?`}
              items={filteredDataList
                .filter(item => selectedIds.includes(item.id))
                .map(item => item.name)}
              confirmText="Delete"
              confirmColor="error"
            />

            <Button
              display="none"
              variant="contained"
              size="large"
              startIcon={<Add />}
              fullWidth={isMobile}
              onClick={openModalFn}
              sx={{
                backgroundColor: "secondary.main",
                fontWeight: "600",
                "&:hover": { backgroundColor: "secondary.dark" },
                whiteSpace: "nowrap",
                hidden: "true",
                fontSize: 14,
              }}
            >
              Add Department
            </Button>
            {/* </Link> */}
          </Stack>
        </Box>
        <DataContext.Provider value={filteredDataList}>
          <Listing
            handleShowButton={handleShowButton}
            openEditModal={openEditModal}
          />
        </DataContext.Provider>

        <Modal
          open={showModal}
          onClose={handleClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 500 } }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Fade in={showModal} timeout={500}>
            <Box
              sx={{
                bgcolor: "background.paper",
                boxShadow: 24,
                px: { xs: 2, sm: 3 },
                pt: { xs: 2, sm: 3 },
                borderRadius: 2,
                position: "relative",
                overflowY: "auto",
                width: { xs: "95%", sm: "80%", md: "50%" },
                maxHeight: "90vh",
              }}
            >
              {/* Header */}
              <Box mb={1}>
                <Typography
                  variant="subtitle"
                  sx={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    mb: 3,
                  }}
                >
                  {mode === 'create' ? "Create New Department" : "Edit Department"}
                </Typography>

                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 12,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <Close />
                </IconButton>
              </Box>
              <Form
                mode={mode}
                onModeChange={handleClose}
                onSuccess={fetchDataAndUpdateList}
                departmentId={mode === 'edit' ? selectedDept.departmentId : 0}
                departmentDetails={mode === 'edit' ? selectedDept : null}
              />

            </Box>
          </Fade>
        </Modal>

      </Box>
    </PrivateLayout>
  );
};

export default Index;
